import { 
  Component, 
  OnInit, 
  signal, 
  computed, 
  effect, 
  inject, 
  DestroyRef,
  ChangeDetectionStrategy 
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { combineLatest, filter, map, switchMap } from 'rxjs';
import { Header } from "../../header/header";
import { Footer } from "../../footer/footer";
import { Survey } from "../../components/survey/survey";
import { ThematicService } from '../../services/thematic.service';
import { PostService } from '../../services/post.service';
import { TimeAgoPipe } from '../../pipes/time-ago.pipe';

registerLocaleData(localeEs);

// Type definitions for better type safety
interface ThematicColors {
  solid_color: string;
  gradient_start?: string;
  gradient_end?: string;
}

interface Thematic {
  id: number;
  name: string;
  description?: string;
  colors: ThematicColors;
  icon?: string;
}

interface ThematicsResponse {
  data: {
    thematics: Thematic[];
  };
}

interface User {
  id: number;
  name: string;
  picture: string;
  email?: string;
}

interface QuickAction {
  id: number;
  name: string;
  url?: string;
  colors: ThematicColors;
  icon?: string;
}

interface PostInfo {
  post_type: 'IMAGE' | 'VIDEO' | 'GIF' | 'TEXT' | 'SURVEY';
  url?: string;
  text?: string;
  thumbnail?: string;
  options?: any[];
}

interface Post {
  id: number;
  user: User;
  created_date: number;
  thematic?: Thematic;
  post_info: PostInfo;
  type: 'IMAGE' | 'VIDEO' | 'TEXT' | 'SURVEY';
  quick_action?: QuickAction;
  likes?: number;
  comments?: number;
}

interface PostsResponse {
  data: {
    posts: Post[];
  };
}

@Component({
  selector: 'app-posts',
  standalone: true,
  imports: [Header, Footer, Survey, TimeAgoPipe, CommonModule],
  templateUrl: './posts.html',
  styleUrl: './posts.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Posts implements OnInit {
  // Dependency injection using inject() (Angular 14+)
  private readonly route = inject(ActivatedRoute);
  private readonly thematicService = inject(ThematicService);
  private readonly postService = inject(PostService);
  private readonly destroyRef = inject(DestroyRef);

  // Signals for reactive state management (Angular 16+)
  public gsid = signal<number>(0);
  public thematics = signal<Thematic[]>([]);
  public allPosts = signal<Post[]>([]);
  public selectedThematic = signal<Thematic | null>(null);
  public isLoadingThematics = signal<boolean>(false);
  public isLoadingPosts = signal<boolean>(false);
  public errorThematics = signal<string | null>(null);
  public errorPosts = signal<string | null>(null);

  // Convert route params to signal (Angular 16+)
  public postId = toSignal(
    this.route.params.pipe(
      map(params => params['id'] ? Number(params['id']) : 0)
    ),
    { initialValue: 0 }
  );

  // Computed signals for derived state
  public hasThematics = computed(() => this.thematics().length > 0);
  public hasPosts = computed(() => this.allPosts().length > 0);
  
  public filteredPosts = computed(() => {
    const selected = this.selectedThematic();
    const posts = this.allPosts();
    
    if (!selected) {
      return posts;
    }
    
    return posts.filter(post => post.thematic?.id === selected.id);
  });

  public isThematicSelected = computed(() => this.selectedThematic() !== null);
  
  public postsCount = computed(() => ({
    total: this.allPosts().length,
    filtered: this.filteredPosts().length
  }));

  public isEmpty = computed(() => 
    !this.isLoadingPosts() && 
    !this.hasPosts() && 
    !this.errorPosts()
  );

  public showEmptyFiltered = computed(() => 
    this.isThematicSelected() && 
    this.filteredPosts().length === 0 &&
    !this.isLoadingPosts()
  );

  constructor() {
    // Effect to load data when gsid changes
    effect(() => {
      const gsidValue = this.gsid();
      if (gsidValue > 0) {
        this.loadThematics(gsidValue);
      }
    }, { allowSignalWrites: true });

    // Effect to load posts when gsid or postId changes
    effect(() => {
      const gsidValue = this.gsid();
      const postIdValue = this.postId();
      
      if (gsidValue > 0) {
        this.loadPosts(gsidValue, postIdValue);
      }
    }, { allowSignalWrites: true });

    // Initialize gsid from localStorage
    this.initializeGsid();
  }

  ngOnInit(): void {
    this.logComponentInit();
  }

  private initializeGsid(): void {
    try {
      const storedGsid = Number(localStorage.getItem('gsid') || '0');
      if (storedGsid > 0) {
        this.gsid.set(storedGsid);
      } else {
        console.warn('Invalid GSID from localStorage');
        this.errorPosts.set('GSID no válido. Por favor, inicia sesión nuevamente.');
      }
    } catch (error) {
      console.error('Error reading GSID from localStorage:', error);
      this.errorPosts.set('Error al cargar la configuración');
    }
  }

  private loadThematics(gsid: number): void {
    console.log('Loading thematics for GSID:', gsid);
    this.isLoadingThematics.set(true);
    this.errorThematics.set(null);

    this.thematicService.getThematics(gsid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: ThematicsResponse) => {
          this.isLoadingThematics.set(false);
          
          if (response?.data?.thematics && Array.isArray(response.data.thematics)) {
            this.thematics.set(response.data.thematics);
            console.log(`Successfully loaded ${response.data.thematics.length} thematics`);
          } else {
            console.warn('Invalid thematics response structure:', response);
            this.thematics.set([]);
          }
        },
        error: (error) => {
          console.error('Error loading thematics:', error);
          this.isLoadingThematics.set(false);
          this.errorThematics.set('Error al cargar las temáticas');
          this.thematics.set([]);
        }
      });
  }

  private loadPosts(gsid: number, postId: number = 0): void {
    console.log('Loading posts for GSID:', gsid, 'PostID:', postId);
    this.isLoadingPosts.set(true);
    this.errorPosts.set(null);

    this.postService.getPosts(gsid, postId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: PostsResponse) => {
          this.isLoadingPosts.set(false);
          
          if (response?.data?.posts && Array.isArray(response.data.posts)) {
            this.allPosts.set(response.data.posts);
            console.log(`Successfully loaded ${response.data.posts.length} posts`);
          } else {
            console.warn('Invalid posts response structure:', response);
            this.allPosts.set([]);
          }
        },
        error: (error) => {
          console.error('Error loading posts:', error);
          this.isLoadingPosts.set(false);
          this.errorPosts.set('Error al cargar las publicaciones. Por favor, intenta de nuevo.');
          this.allPosts.set([]);
        }
      });
  }

  public toggleThematicFilter(thematic: Thematic): void {
    const current = this.selectedThematic();
    
    if (current?.id === thematic.id) {
      // Deselect if clicking the same thematic
      this.selectedThematic.set(null);
      console.log('Cleared thematic filter');
    } else {
      // Select new thematic
      this.selectedThematic.set(thematic);
      console.log('Filtering by thematic:', thematic.name);
    }
  }

  public clearThematicFilter(): void {
    this.selectedThematic.set(null);
  }

  public openQuickAction(quickAction: QuickAction | undefined): void {
    if (!quickAction?.url) {
      console.warn('Quick action has no URL');
      return;
    }

    try {
      // Intentar abrir la URL en una nueva pestaña
      window.open(quickAction.url, '_blank', 'noopener,noreferrer');
      
      // No mostrar error automáticamente ya que window.open() puede retornar null
      // por políticas de seguridad del navegador incluso cuando se abre correctamente.
      // El usuario verá si la pestaña se abre o no.
      
    } catch (error) {
      console.error('Error opening quick action:', error);
    }
  }

  public retryLoadPosts(): void {
    const gsidValue = this.gsid();
    const postIdValue = this.postId();
    if (gsidValue > 0) {
      this.loadPosts(gsidValue, postIdValue);
    }
  }

  public retryLoadThematics(): void {
    const gsidValue = this.gsid();
    if (gsidValue > 0) {
      this.loadThematics(gsidValue);
    }
  }

  public clearPostsError(): void {
    this.errorPosts.set(null);
  }

  public clearThematicsError(): void {
    this.errorThematics.set(null);
  }

  public getThematicColor(thematic: Thematic | undefined): string {
    return thematic?.colors?.solid_color ? `#${thematic.colors.solid_color}` : '#ccc';
  }

  public trackByThematicId(index: number, thematic: Thematic): number {
    return thematic.id;
  }

  public trackByPostId(index: number, post: Post): number {
    return post.id;
  }

  private logComponentInit(): void {
    console.log('Posts component initialized with GSID:', this.gsid(), 'PostID:', this.postId());
  }
}
